/* @flow */
import { observable, action, computed, intercept, toJS } from 'mobx';

export type TypeAutoCompleteItem = {
  id: number | string,
  name: string,
};

type TypeSource = (string, ?number | ?string) => Promise<Array<TypeAutoCompleteItem>>;

type TypeChosen = {|
  id: string | number,
  name: string,
|};

export type TypeParams = {|
  source: 'click' | 'change',
|};

export default class AutoCompleteData {
  source: TypeSource;

  @observable loading: boolean = false;

  @observable success: boolean = false;

  @observable search: string = '';

  @observable chosen: *;

  @observable selected: ?string | ?number = null;

  @observable data: Array<TypeAutoCompleteItem> = [];

  @observable filterId: string = '';

  @computed get list(): Array<TypeAutoCompleteItem> {
    return toJS(this.data);
  }

  constructor(source: TypeSource, parentAutoComplete?: AutoCompleteData) {
    if (parentAutoComplete) {
      this.source = (search: string): Promise<Array<TypeAutoCompleteItem>> => {
        return source(search, null);
      };

      intercept(
        parentAutoComplete,
        'selected',
        (changed: Object): Object => {
          // clear data if parent changed
          this.selected = null;
          this.search = '';
          this.data = [];
          // update source
          this.source = (search: string): Promise<Array<TypeAutoCompleteItem>> => {
            return source(search, changed.newValue);
          };

          return changed;
        }
      );
    } else {
      this.source = source;
    }
  }

  setSearch(search: string) {
    this.search = search;

    if (search) {
      this.loadData(search);
    }
  }

  @action async loadData(search: string, filterId: string): Promise<Object> {
    this.loading = true;
    this.success = false;
    this.data = await this.source(this.search, this.filterId);

    if (this.data) {
      this.success = true;
    }

    this.loading = false;

    if (!this.data) {
      return [];
    }

    if (this.data.data) {
      return { options: toJS(this.data.data) };
    } else if (this.data.active) {
      return { options: toJS(this.data.active) };
    } else {
      return { options: toJS(this.data) };
    }
  }

  @action onUpdate(search: string) {
    this.setSearch(search);
  }

  @action setResult(chosen: TypeChosen) {
    this.selected = chosen ? chosen.id : null;
    this.search = chosen ? chosen.name : '';
    this.chosen = chosen;
  }

  @action onFocus() {
    // RESET SEARCH ON FOCUS
    this.data = [];
    this.setSearch('');
    this.loadData('');
  }

  @action clear() {
    this.search = '';
    this.selected = null;
    this.data = [];
    this.filterId = '';
    this.chosen = null;
  }
}
